import { useTranslation } from '@multaqa/i18n'
import type { GroupDefaults as GroupDefaultsData } from './api'
import type { SettingsFormValues } from './group-defaults-settings-fields'
import { SystemHint } from './group-defaults-settings-fields'

export type { SettingsFormValues } from './group-defaults-settings-fields'

export function SettingsReadOnly({ data }: { data: GroupDefaultsData }) {
  const { t } = useTranslation()
  const rows: [string, unknown, unknown, ((value: unknown) => string)?][] = [
    [
      t('groups.defaults.settings.fields.paymentTerms'),
      data.default_payment_terms_days,
      data.system_defaults.default_payment_terms_days,
    ],
    [
      t('groups.defaults.settings.fields.allowNegativeStock'),
      data.allow_negative_stock,
      data.system_defaults.allow_negative_stock,
      (value) => t(`groups.defaults.settings.values.${value ? 'enabled' : 'disabled'}`),
    ],
    [
      t('groups.defaults.settings.fields.expiryConvention'),
      data.default_expiry_convention,
      data.system_defaults.default_expiry_convention,
      (value) => t(`groups.defaults.settings.values.${value}`),
    ],
    [
      t('groups.defaults.settings.fields.expiryWarningDays'),
      data.expiry_warning_days,
      data.system_defaults.expiry_warning_days,
    ],
    [
      t('groups.defaults.settings.fields.allocationStrategy'),
      data.default_allocation_strategy,
      data.system_defaults.default_allocation_strategy,
      (value) => t(`groups.defaults.settings.values.${value}`),
    ],
    [
      t('groups.defaults.settings.fields.overReceiptPolicy'),
      data.over_receipt_policy,
      data.system_defaults.over_receipt_policy,
      (value) => t(`groups.defaults.settings.values.${value}`),
    ],
    [
      t('groups.defaults.settings.fields.permissionMonthlyMinutes'),
      data.permission_monthly_minutes,
      data.system_defaults.permission_monthly_minutes,
    ],
    [
      t('groups.defaults.settings.fields.brandingAccentColor'),
      data.branding_accent_color,
      data.system_defaults.branding_accent_color,
    ],
  ]
  return (
    <div className="grid gap-x-6 sm:grid-cols-2" data-testid="group-defaults-settings-view">
      {rows.map(([label, value, system, format]) => (
        <div key={label} className="border-b border-border py-3">
          <div className="text-label text-muted-foreground font-mono uppercase">{label}</div>
          <SettingValue value={value} system={system} format={format} />
        </div>
      ))}
    </div>
  )
}
function SettingValue({
  value,
  system,
  format,
}: {
  value: unknown
  system: unknown
  format?: (value: unknown) => string
}) {
  return value == null ? (
    <SystemHint value={system} />
  ) : (
    <div className="text-body font-mono">{format ? format(value) : String(value)}</div>
  )
}
export function fromDefaults(data: GroupDefaultsData): SettingsFormValues {
  return {
    default_payment_terms_days: data.default_payment_terms_days,
    allow_negative_stock: data.allow_negative_stock,
    default_expiry_convention: data.default_expiry_convention as 'on' | 'after' | null,
    expiry_warning_days: data.expiry_warning_days,
    default_allocation_strategy: data.default_allocation_strategy as 'fefo' | 'fifo' | null,
    over_receipt_policy: data.over_receipt_policy as 'block' | 'allow' | null,
    permission_monthly_minutes: data.permission_monthly_minutes,
    branding_accent_color: data.branding_accent_color,
  }
}
export function emptyDefaults(): SettingsFormValues {
  return {
    default_payment_terms_days: null,
    allow_negative_stock: null,
    default_expiry_convention: null,
    expiry_warning_days: null,
    default_allocation_strategy: null,
    over_receipt_policy: null,
    permission_monthly_minutes: null,
    branding_accent_color: null,
  }
}
